/**
 * Доля хвоста публичного рендерера: вид `gallery` (галерея картинок).
 *
 * WEB-2920, заход 2: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе с плиткой `GalleryTile`. Метка `case 'gallery'` и хуки остаются в фасаде; ветка вида —
 * функция отрисовки `renderGallery(ctx)`, замыкания фасада приходят
 * объектом-контекстом (`ExtendedRenderContext`). Эту долю импортирует только
 * фасад: она едет в ленивом куске `extended`, а не в первом экране
 * (самопроверка обхода).
 */
import React, { useEffect, useState } from 'react';
import { AuthorHintBox } from '../AuthorHintBox';
import { useEditorPreview } from '../EditorPreviewContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { getExtendedBlockStrings } from '@/utils/publicExtendedBlockStrings';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

/**
 * WEB-2380 п.7 — ОДНА ПЛИТКА ГАЛЕРЕИ со своей памятью об ошибке загрузки.
 *
 * Плитки рисуются списком, а состояние «эта картинка не загрузилась» нужно
 * КАЖДОЙ по отдельности — хук внутри `map` поставить нельзя (правила хуков),
 * поэтому плитка вынесена отдельным компонентом. Мёртвый адрес (снятая ссылка
 * поставщика) даёт понятную заглушку вместо молчаливой дыры — ровно так же,
 * как у одиночного блока `image` (WEB-674).
 */
function GalleryTile({
  src,
  style,
  lazy,
  errorText,
}: {
  src: string;
  style: React.CSSProperties;
  lazy: boolean;
  errorText: string;
}): React.ReactElement {
  const [failed, setFailed] = useState(false);
  // Автор поправил ссылку — прежняя ошибка к новой картинке отношения не имеет.
  useEffect(() => {
    setFailed(false);
  }, [src]);
  if (failed) {
    return (
      <AuthorHintBox style={{ background: 'var(--color-bg-surface, #fafafa)' }}>
        {errorText}
      </AuthorHintBox>
    );
  }
  return (
    <img
      src={src}
      alt=""
      decoding="async"
      loading={lazy ? 'lazy' : undefined}
      onError={() => setFailed(true)}
      style={style}
    />
  );
}

/**
 * WEB-2451 п.4 (хвост WEB-1428): галерея без единой картинки на опубликованной
 * странице не рисуется вовсе — как «Изображение» без адреса; заготовка-заглушка
 * остаётся только автору на холсте конструктора (признак — `useEditorPreview`).
 * Отдельный компонент: ветка `renderGallery` — функция, хук в ней не позвать.
 */
function EmptyGallery({
  style,
  testId,
  text,
}: {
  style: React.CSSProperties;
  testId: string;
  text: string;
}): React.ReactElement | null {
  const { isEditorPreview } = useEditorPreview();
  if (!isEditorPreview) return null;
  return (
    <div data-testid={testId} style={style}>
      <div
        style={{
          gridColumn: '1 / -1',
          background: '#f5f5f5',
          borderRadius: 'var(--border-radius-md, 8px)',
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#bfbfbf',
        }}
      >
        {/* WEB-2936: было зашито по-английски и для русской воронки. */}
        {text}
      </div>
    </div>
  );
}

/** Ветка `gallery` фасада — тело `case` слово в слово. */
export function renderGallery(ctx: ExtendedRenderContext): React.ReactElement {
  const { block, varCtx } = ctx;
  // WEB-2380 п.7: пустые заготовки слотов (`['', '', '']` от модели)
  // рисовались битыми клетками сетки. Показываем только РЕАЛЬНЫЕ
  // адреса; пусто по смыслу — то же, что отсутствие поля (тот же
  // предикат, что у сервера в `block-schema/image-url-fields.ts`).
  const imgs = (block.galleryImages ?? []).filter(
    (u) => typeof u === 'string' && u.trim() !== '',
  );
  // WEB-695: galleryAlign — при заданном выравнивании flex-раскладка
  // (заметно при 1-2 картинках); без него — прежняя сетка 3 колонки.
  const galleryStyle: React.CSSProperties = block.galleryAlign
    ? {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent:
          block.galleryAlign === 'center'
            ? 'center'
            : block.galleryAlign === 'right'
              ? 'flex-end'
              : 'flex-start',
        gap: 8,
        marginBottom: 16,
      }
    : {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
        marginBottom: 16,
      };
  const imgStyle: React.CSSProperties = block.galleryAlign
    ? {
        width: 'calc(33.33% - 6px)',
        aspectRatio: '1',
        objectFit: 'cover',
        borderRadius: 'var(--radius-image, var(--border-radius-md, 8px))',
        display: 'block',
      }
    : {
        width: '100%',
        aspectRatio: '1',
        objectFit: 'cover',
        borderRadius: 'var(--radius-image, var(--border-radius-md, 8px))',
        display: 'block',
      };
  if (imgs.length === 0) {
    return (
      <EmptyGallery
        style={galleryStyle}
        testId={`gallery-${block.id}`}
        text={getExtendedBlockStrings(varCtx?.system?.locale).galleryEmpty}
      />
    );
  }
  return (
    <div data-testid={`gallery-${block.id}`} style={galleryStyle}>
      {imgs.map((img, i) => (
        <GalleryTile
          key={i}
          src={img}
          style={imgStyle}
          // WEB-1994: ленивая загрузка ТОЛЬКО ниже первого ряда. Первый
          // ряд галереи (три плитки) может быть первым экраном и нести
          // главную картинку — её ленивой делать нельзя.
          lazy={i >= 3}
          errorText={
            getPublicQuizStrings(varCtx?.system?.locale).imageLoadError
          }
        />
      ))}
    </div>
  );
}
